import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useEasyMode } from "@/contexts/EasyModeContext";

export interface ShortcutDef {
  key: string;
  label: string;
  category: "voucher" | "action" | "navigation";
  description: string;
  action: (nav: ReturnType<typeof useNavigate>) => void;
}

export const GLOBAL_SHORTCUTS: ShortcutDef[] = [
  // Function-key voucher shortcuts (Tally-style)
  { key: "F4", label: "F4", category: "voucher", description: "Contra Entry", action: (nav) => nav("/dashboard/journals?type=contra&action=new") },
  { key: "F5", label: "F5", category: "voucher", description: "Payment Voucher", action: (nav) => nav("/dashboard/journals?type=payment&action=new") },
  { key: "F6", label: "F6", category: "voucher", description: "Receipt Voucher", action: (nav) => nav("/dashboard/journals?type=receipt&action=new") },
  { key: "F7", label: "F7", category: "voucher", description: "Journal Voucher", action: (nav) => nav("/dashboard/journals?type=journal&action=new") },
  { key: "F8", label: "F8", category: "voucher", description: "Sales Voucher", action: (nav) => nav("/dashboard/sales") },
  { key: "F9", label: "F9", category: "voucher", description: "Purchase Voucher", action: (nav) => nav("/dashboard/purchase") },
  // Ctrl shortcuts
  { key: "ctrl+e", label: "Ctrl+E", category: "action", description: "Export Data", action: (nav) => nav("/dashboard/export") },
  // Navigation
  { key: "ctrl+k", label: "Ctrl+K", category: "navigation", description: "Command Palette", action: () => {} }, // handled elsewhere
  { key: "F1", label: "F1", category: "navigation", description: "Shortcuts Help", action: () => {} }, // handled by help dialog toggle
];

export function useGlobalShortcuts() {
  const navigate = useNavigate();
  const { isEasyMode } = useEasyMode();
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT" || target.isContentEditable;

      // F1 → toggle help (always)
      if (e.key === "F1") {
        e.preventDefault();
        setShowHelp(v => !v);
        return;
      }

      // F2 → focus date field
      if (e.key === "F2") {
        e.preventDefault();
        const dateInput = document.querySelector<HTMLInputElement>('input[type="date"], [data-date-field]');
        if (dateInput) {
          dateInput.focus();
          dateInput.click();
          toast({ title: "Date Field", description: "Date field focused. Change the date." });
        } else {
          toast({ title: "No Date Field", description: "No date field found on this page.", variant: "destructive" });
        }
        return;
      }

      // Ctrl+S → Save (trigger submit on closest form)
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        const form = document.querySelector<HTMLFormElement>("form");
        const submitBtn = document.querySelector<HTMLButtonElement>('button[type="submit"], [data-save-btn]');
        if (submitBtn) {
          submitBtn.click();
          toast({ title: "Saving...", description: "Entry saved via Ctrl+S" });
        } else if (form) {
          form.requestSubmit();
        } else {
          toast({ title: "Nothing to save", description: "No active form found on this page." });
        }
        return;
      }

      // Ctrl+P → Print
      if ((e.ctrlKey || e.metaKey) && e.key === "p") {
        e.preventDefault();
        window.print();
        return;
      }

      // Ctrl+E → Export
      if ((e.ctrlKey || e.metaKey) && e.key === "e") {
        e.preventDefault();
        navigate("/dashboard/export");
        return;
      }

      // Function keys for vouchers — only when not in input
      if (!isInput && e.key.startsWith("F") && !e.ctrlKey && !e.altKey) {
        // In Easy Mode, voucher transactions are opened as modals without navigating or changing URL
        if (isEasyMode && ["F4", "F5", "F6", "F7", "F8", "F9"].includes(e.key)) {
          return;
        }

        const shortcut = GLOBAL_SHORTCUTS.find(s => s.key === e.key);
        if (shortcut && shortcut.category === "voucher") {
          e.preventDefault();
          shortcut.action(navigate);
          toast({ title: shortcut.description, description: `Opened via ${shortcut.label}` });
        }
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  return { showHelp, setShowHelp };
}
